import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PiCalendarBlank, PiArrowRight, PiLightbulb, PiCaretDoubleDown } from 'react-icons/pi';
import { eventService } from '../../../services/event.service';
import styles from './PublicEventsList.module.css';

const getImageSrc = (url) => {
    if (!url) return null;
    if (url.startsWith('data:') || url.startsWith('http') || url.startsWith('/')) return url;
    return `data:image/jpeg;base64,${url}`;
};

const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('uk-UA', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
};

const formatDateRange = (start, end) => {
    const s = formatDate(start);
    const e = formatDate(end);
    if (!s && !e) return '';
    if (!e) return s;
    return `${s} — ${e}`;
};

function ActiveCard({ event, t }) {
    const src = getImageSrc(event.cover_image);
    const tags = event.tags ?? [];

    return (
        <div className={styles.activeCard}>
            {src && (
                <div className={styles.activeImageWrap}>
                    <img src={src} alt={event.title} className={styles.activeImage} />
                </div>
            )}
            <div className={`${styles.activeBody} ${!src ? styles.activeBodyFull : ''}`}>
                <span className={styles.activeBadge}>
                    <span className={styles.activeDot} />
                    {t('events.status_now')}
                </span>
                <h3 className={styles.activeTitle}>{event.title}</h3>
                {event.description && (
                    <p className={styles.activeDescription}>{event.description}</p>
                )}
                <div className={styles.metaRow}>
                    <PiCalendarBlank className={styles.metaIcon} />
                    <span>{formatDateRange(event.start_date, event.end_date)}</span>
                </div>
                {tags.length > 0 && (
                    <div className={styles.tags}>
                        {tags.map((tag, i) => (
                            <span key={i} className={styles.tag}>
                                {tag.name ?? tag.code ?? tag}
                            </span>
                        ))}
                    </div>
                )}
                <div className={styles.buttonsRow}>
                    <Link to={`/events/${event.id}`} className={styles.primaryButton}>
                        {t('events.view_details')}
                        <PiArrowRight className={styles.buttonIcon} />
                    </Link>
                    <Link to="/" className={styles.publishBtn}>
                        <PiLightbulb className={styles.buttonIcon} />
                        {t('events.publish_idea')}
                    </Link>
                </div>
            </div>
        </div>
    );
}

function UpcomingCard({ event, t }) {
    const src = getImageSrc(event.cover_image);
    const tags = event.tags ?? [];

    return (
        <div className={styles.upcomingCard}>
            {src && (
                <div className={styles.upcomingImageWrap}>
                    <img src={src} alt={event.title} className={styles.upcomingImage} />
                </div>
            )}
            <div className={`${styles.upcomingBody} ${!src ? styles.upcomingBodyFull : ''}`}>
                <h3 className={styles.upcomingTitle}>{event.title}</h3>
                {event.description && (
                    <p className={styles.upcomingDescription}>{event.description}</p>
                )}
                <div className={styles.metaRow}>
                    <PiCalendarBlank className={styles.metaIcon} />
                    <span>{formatDateRange(event.start_date, event.end_date)}</span>
                </div>
                {tags.length > 0 && (
                    <div className={styles.tags}>
                        {tags.map((tag, i) => (
                            <span key={i} className={styles.tag}>
                                {tag.name ?? tag.code ?? tag}
                            </span>
                        ))}
                    </div>
                )}
                <div className={styles.buttonsRow}>
                    <Link to={`/events/${event.id}`} className={styles.ghostLink}>
                        {t('events.view_details')}
                        <PiArrowRight className={styles.buttonIcon} />
                    </Link>
                    <Link to="/" className={styles.publishBtn}>
                        <PiLightbulb className={styles.buttonIcon} />
                        {t('events.publish_idea')}
                    </Link>
                </div>
            </div>
            <span className={styles.upcomingBadge}>{t('events.status_upcoming_label')}</span>
        </div>
    );
}

function PublicEventsList() {
    const { t } = useTranslation('common');
    const { t: tValidation } = useTranslation('validation');
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            try {
                const data = await eventService.getPublicEvents();
                setEvents(data?.events ?? data ?? []);
            } catch (err) {
                const errorKey = err?.payload?.error;
                setError(typeof errorKey === 'string' ? errorKey : 'network_error');
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, []);

    const now = new Date();
    const activeEvents = events.filter(
        (e) => e.start_date && e.end_date &&
            now >= new Date(e.start_date) && now <= new Date(e.end_date)
    );
    const upcomingEvents = events.filter(
        (e) => e.start_date && now < new Date(e.start_date)
    );
    const hasEvents = activeEvents.length > 0 || upcomingEvents.length > 0;

    return (
        <div className={styles.page}>
            <nav className={styles.navbar}>
                <div className={styles.logoWrap}>
                    <img src="/assets/menu-logo-mini.svg" alt="Logo" className={styles.logoIcon} />
                    <span className={styles.logoText}>IDEARUM</span>
                </div>
                <Link to="/" className={styles.signInLink}>
                    {t('events.sign_in')}
                </Link>
            </nav>

            <section
                className={styles.hero}
                style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/auth-background.png)` }}
            >
                <div className={styles.heroOverlay} />
                <div className={styles.heroContent}>
                    <p className={styles.heroTagline}>ІННОВАЦІЇ&nbsp;•&nbsp;НАУКА&nbsp;•&nbsp;ІДЕЇ</p>
                    <h1 className={styles.heroTitle}>
                        Досліджуй інноваційні<br />
                        <span className={styles.heroTitleAccent}>Події</span>
                    </h1>
                    <p className={styles.heroSubtitle}>
                        Відкривай ідеї, об'єднуй думки, твори майбутнє
                    </p>
                </div>
                <button
                    className={styles.scrollCta}
                    onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
                    aria-label="Scroll down"
                >
                    <PiCaretDoubleDown className={styles.scrollCtaIcon} />
                </button>
            </section>

            <main className={styles.content}>
                {isLoading && (
                    <div className={styles.stateBox}>
                        <div className={styles.spinner} />
                        <p className={styles.stateText}>Завантаження...</p>
                    </div>
                )}

                {!isLoading && error && (
                    <div className={styles.stateBox}>
                        <p className={styles.errorText}>{tValidation(error)}</p>
                    </div>
                )}

                {!isLoading && !error && !hasEvents && (
                    <div className={styles.stateBox}>
                        <p className={styles.stateText}>{t('events.no_active_events')}</p>
                    </div>
                )}

                {!isLoading && !error && activeEvents.length > 0 && (
                    <section className={styles.section}>
                        <h2 className={styles.sectionTitle}>{t('events.active_section')}</h2>
                        <div className={styles.activeList}>
                            {activeEvents.map((event) => (
                                <ActiveCard key={event.id} event={event} t={t} />
                            ))}
                        </div>
                    </section>
                )}

                {!isLoading && !error && upcomingEvents.length > 0 && (
                    <section className={styles.section}>
                        <h2 className={styles.sectionTitle}>{t('events.upcoming_section')}</h2>
                        <div className={styles.upcomingList}>
                            {upcomingEvents.map((event) => (
                                <UpcomingCard key={event.id} event={event} t={t} />
                            ))}
                        </div>
                    </section>
                )}
            </main>
        </div>
    );
}

export default PublicEventsList;
